import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
  Pill, Plus, Search, Filter, Eye, CheckCircle, XCircle, Clock,
  User, Calendar, RefreshCw, ArrowLeft, Save, Trash2, Download, FileText, Edit, Copy, Maximize2, Minimize2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PatientSearchDropdown } from '@/components/SearchDropdowns';
import ICDCodeSuggester from '@/components/ICDCodeSuggester';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';

export default function Prescriptions() {
  const navigate = useNavigate();
  const { prescriptionId } = useParams();
  const [searchParams] = useSearchParams();
  const [prescriptions, setPrescriptions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('list');
  
  // Detail view state
  const [prescriptionDetail, setPrescriptionDetail] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPrescription, setEditingPrescription] = useState(null);
  const [editFormData, setEditFormData] = useState({
    diagnosis: '',
    instructions: '',
    follow_up_date: '',
    notes: ''
  });
  const [editMedications, setEditMedications] = useState([]);
  
  const user = JSON.parse(localStorage.getItem('user'));
  
  // Form state for new prescription
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [visits, setVisits] = useState([]);
  const [patients, setPatients] = useState([]);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [selectedIcdCodes, setSelectedIcdCodes] = useState([]);
  
  const [formData, setFormData] = useState({
    visit_id: searchParams.get('visit_id') || '',
    patient_id: searchParams.get('patient_id') || '',
    diagnosis: '',
    instructions: '',
    follow_up_date: '',
    notes: ''
  });
  
  const [medications, setMedications] = useState([
    { name: '', dosage: '', frequency: '', duration: '', route: 'oral', instructions: '', quantity: '' }
  ]);

  // Handle ICD code selection
  const handleIcdCodeSelect = (code, allSelected) => {
    setSelectedIcdCodes(allSelected);
    // Auto-update diagnosis field with ICD codes
    if (allSelected.length > 0) {
      const diagnosisText = allSelected.map(c => `${c.code} - ${c.description}`).join('; ');
      setFormData(prev => ({ ...prev, diagnosis: diagnosisText }));
    }
  };

  useEffect(() => {
    fetchData();
    
    // Auto-open create dialog if visit_id is provided
    if (searchParams.get('visit_id')) {
      setCreateDialogOpen(true);
      fetchVisitDetails(searchParams.get('visit_id'));
    }
  }, []);

  // Load prescription details if prescriptionId is in URL
  useEffect(() => {
    if (prescriptionId && !searchParams.get('visit_id')) {
      fetchPrescriptionDetail(prescriptionId);
    }
  }, [prescriptionId]);

  const fetchPrescriptionDetail = async (id) => {
    try {
      const response = await api.get(`/prescriptions/${id}`);
      setPrescriptionDetail(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch prescription details');
      navigate('/prescriptions');
    }
  };

  const handleViewPrescription = async (id) => {
    await fetchPrescriptionDetail(id);
  };

  const handleEditPrescription = async (rx) => {
    setEditingPrescription(rx);
    setEditFormData({
      diagnosis: rx.diagnosis || '',
      instructions: rx.instructions || '',
      follow_up_date: rx.follow_up_date || '',
      notes: rx.notes || ''
    });
    setEditMedications(rx.medications || []);
    setEditDialogOpen(true);
  };

  const handleUpdatePrescription = async (e) => {
    e.preventDefault();
    if (!editingPrescription) return;
    
    try {
      await api.put(`/prescriptions/${editingPrescription.id}`, {
        ...editFormData,
        medications: editMedications
      });
      toast.success('Prescription updated successfully!');
      setEditDialogOpen(false);
      setEditingPrescription(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update prescription');
    }
  };

  const handleClonePrescription = (rx) => {
    // Pre-fill the create form with data from the prescription being cloned
    setFormData({
      visit_id: '', // User must select a new visit
      patient_id: rx.patient_id,
      diagnosis: rx.diagnosis || '',
      instructions: rx.instructions || '',
      follow_up_date: '',
      notes: `Cloned from ${rx.prescription_number}`
    });
    // Clone medications (create new objects to avoid reference issues)
    setMedications(rx.medications?.map(med => ({
      name: med.name || '',
      dosage: med.dosage || '',
      frequency: med.frequency || '',
      duration: med.duration || '',
      route: med.route || 'oral',
      instructions: med.instructions || '',
      quantity: med.quantity || ''
    })) || [{ name: '', dosage: '', frequency: '', duration: '', route: 'oral', instructions: '', quantity: '' }]);
    
    setCreateDialogOpen(true);
    toast.info('Prescription cloned! Select a visit and review before saving.');
  };

  const addEditMedication = () => {
    setEditMedications([...editMedications, { name: '', dosage: '', frequency: '', duration: '', route: 'oral', instructions: '', quantity: '' }]);
  };

  const updateEditMedication = (index, field, value) => {
    const updated = [...editMedications];
    updated[index][field] = value;
    setEditMedications(updated);
  };

  const removeEditMedication = (index) => {
    if (editMedications.length > 1) {
      setEditMedications(editMedications.filter((_, i) => i !== index));
    }
  };

  const fetchData = async () => {
    try {
      const [prescriptionsRes, statsRes, patientsRes, visitsRes] = await Promise.all([
        api.get('/prescriptions'),
        api.get('/prescriptions/stats'),
        api.get('/patients'),
        api.get('/visits')  // Fetch all visits
      ]);
      setPrescriptions(prescriptionsRes.data);
      setStats(statsRes.data);
      setPatients(patientsRes.data);
      // Keep all visits but show active ones at the top for convenience
      const activeStatuses = ['in_progress', 'in_consultation', 'checked_in'];
      const sortedVisits = [...visitsRes.data].sort((a, b) => {
        const aActive = activeStatuses.includes(a.status) ? 0 : 1;
        const bActive = activeStatuses.includes(b.status) ? 0 : 1;
        return aActive - bActive;
      });
      setVisits(sortedVisits);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVisitDetails = async (visitId) => {
    try {
      const response = await api.get(`/visits/${visitId}`);
      setSelectedVisit(response.data);
      setFormData(prev => ({
        ...prev,
        visit_id: visitId,
        patient_id: response.data.patient_id
      }));
    } catch (error) {
      console.error('Failed to fetch visit details');
    }
  };

  const handleVisitChange = async (visitId) => {
    if (visitId && visitId !== '') {
      setFormData(prev => ({ ...prev, visit_id: visitId }));
      await fetchVisitDetails(visitId);
    } else {
      // Clear visit selection but keep patient
      setSelectedVisit(null);
      setFormData(prev => ({ ...prev, visit_id: '' }));
    }
  };

  const addMedication = () => {
    setMedications([...medications, { 
      name: '', dosage: '', frequency: '', duration: '', route: 'oral', instructions: '', quantity: '' 
    }]);
  };

  const removeMedication = (index) => {
    if (medications.length > 1) {
      setMedications(medications.filter((_, i) => i !== index));
    }
  };

  const updateMedication = (index, field, value) => {
    const updated = [...medications];
    updated[index][field] = value;
    setMedications(updated);
  };

  const handleExport = async (format) => {
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await api.get(`/export/prescriptions/${format}${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `prescriptions_export.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Prescriptions exported to ${format.toUpperCase()} successfully!`);
    } catch (error) {
      toast.error('Failed to export prescriptions');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate - either visit_id or patient_id must be present
    if (!formData.visit_id && !formData.patient_id) {
      toast.error('Please select a visit or patient');
      return;
    }
    if (!formData.diagnosis) {
      toast.error('Please enter a diagnosis');
      return;
    }
    if (!medications[0].name) {
      toast.error('Please add at least one medication');
      return;
    }
    
    // Filter out empty medications and prepare data
    const validMedications = medications.filter(m => m.name.trim());
    
    try {
      await api.post('/prescriptions', {
        ...formData,
        prescriber_id: user.id,
        medications: validMedications.map(m => ({
          ...m,
          quantity: m.quantity ? parseInt(m.quantity) : null
        }))
      });
      
      toast.success('Prescription created successfully');
      setCreateDialogOpen(false);
      resetForm();
      fetchData();
      
      // Navigate back to visit if came from there
      if (searchParams.get('visit_id')) {
        navigate(`/visits/${searchParams.get('visit_id')}`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create prescription'));
    }
  };

  const resetForm = () => {
    setFormData({
      visit_id: '',
      patient_id: '',
      diagnosis: '',
      instructions: '',
      follow_up_date: '',
      notes: ''
    });
    setMedications([{ name: '', dosage: '', frequency: '', duration: '', route: 'oral', instructions: '', quantity: '' }]);
    setSelectedVisit(null);
  };

  const handleUpdateStatus = async (prescriptionId, newStatus) => {
    try {
      await api.put(`/prescriptions/${prescriptionId}/status?status=${newStatus}`);
      toast.success(`Prescription marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDownloadPDF = async (prescriptionId, prescriptionNumber) => {
    try {
      const response = await api.get(`/prescriptions/${prescriptionId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${prescriptionNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Prescription PDF downloaded');
    } catch (error) {
      toast.error('Failed to download prescription PDF');
    }
  };

  const filteredPrescriptions = prescriptions.filter(rx => {
    const patient = patients.find(p => p.id === rx.patient_id);
    const patientName = patient?.full_name?.toLowerCase() || '';
    const matchesSearch = rx.prescription_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          rx.diagnosis?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          patientName.includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || rx.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'dispensed': return <CheckCircle className="h-4 w-4" />;
      case 'cancelled': return <XCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'dispensed': return 'bg-emerald-100 text-emerald-700';
      case 'cancelled': return 'bg-rose-100 text-rose-700';
      default: return 'bg-purple-200 text-blue-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  // If coming from visit details page, show form directly
  if (searchParams.get('visit_id') && !loading) {
    return (
      <div data-testid="prescription-form-page">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate(`/visits/${searchParams.get('visit_id')}`)}
              className="mb-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Visit
            </Button>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
              Create Prescription
            </h1>
            {selectedVisit && (
              <p className="text-slate-600 mt-2">
                For: <span className="font-medium">{selectedVisit.patient?.full_name}</span> • Visit: {selectedVisit.visit_number}
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Diagnosis & Instructions */}
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Prescription Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Diagnosis * (AI-powered ICD-10 suggestions)</Label>
                  <ICDCodeSuggester
                    value={formData.diagnosis}
                    onChange={(text) => setFormData({...formData, diagnosis: text})}
                    onSelect={handleIcdCodeSelect}
                    placeholder="Type diagnosis to get ICD-10 code suggestions..."
                  />
                  {selectedIcdCodes.length > 0 && (
                    <p className="text-xs text-slate-500 mt-1">
                      Selected ICD codes: {selectedIcdCodes.map(c => c.code).join(', ')}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Follow-up Date</Label>
                    <Input
                      type="date"
                      value={formData.follow_up_date}
                      onChange={(e) => setFormData({...formData, follow_up_date: e.target.value})}
                      data-testid="followup-date-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Instructions</Label>
                  <textarea
                    value={formData.instructions}
                    onChange={(e) => setFormData({...formData, instructions: e.target.value})}
                    className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                    placeholder="General instructions for the patient..."
                    data-testid="instructions-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Medications */}
            <Card className="border border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-semibold">Medications</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={addMedication}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Medication
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                {medications.map((med, index) => (
                  <div key={index} className="p-4 bg-slate-50 rounded-lg space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-slate-700">Medication {index + 1}</span>
                      {medications.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeMedication(index)}
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Medication Name *</Label>
                        <Input
                          value={med.name}
                          onChange={(e) => updateMedication(index, 'name', e.target.value)}
                          placeholder="e.g., Amoxicillin"
                          data-testid={`med-name-${index}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Dosage *</Label>
                        <Input
                          value={med.dosage}
                          onChange={(e) => updateMedication(index, 'dosage', e.target.value)}
                          placeholder="e.g., 500mg"
                          data-testid={`med-dosage-${index}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Frequency *</Label>
                        <Select value={med.frequency} onValueChange={(v) => updateMedication(index, 'frequency', v)}>
                          <SelectTrigger data-testid={`med-frequency-${index}`}>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="once daily">Once Daily (OD)</SelectItem>
                            <SelectItem value="twice daily">Twice Daily (BD)</SelectItem>
                            <SelectItem value="three times daily">Three Times (TDS)</SelectItem>
                            <SelectItem value="four times daily">Four Times (QID)</SelectItem>
                            <SelectItem value="as needed">As Needed (PRN)</SelectItem>
                            <SelectItem value="before meals">Before Meals (AC)</SelectItem>
                            <SelectItem value="after meals">After Meals (PC)</SelectItem>
                            <SelectItem value="at bedtime">At Bedtime (HS)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Duration *</Label>
                        <Input
                          value={med.duration}
                          onChange={(e) => updateMedication(index, 'duration', e.target.value)}
                          placeholder="e.g., 7 days"
                          data-testid={`med-duration-${index}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Route</Label>
                        <Select value={med.route} onValueChange={(v) => updateMedication(index, 'route', v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="oral">Oral</SelectItem>
                            <SelectItem value="topical">Topical</SelectItem>
                            <SelectItem value="IV">IV</SelectItem>
                            <SelectItem value="IM">IM</SelectItem>
                            <SelectItem value="subcutaneous">Subcutaneous</SelectItem>
                            <SelectItem value="inhaled">Inhaled</SelectItem>
                            <SelectItem value="rectal">Rectal</SelectItem>
                            <SelectItem value="sublingual">Sublingual</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Quantity</Label>
                        <Input
                          type="number"
                          value={med.quantity}
                          onChange={(e) => updateMedication(index, 'quantity', e.target.value)}
                          placeholder="e.g., 21"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Special Instructions</Label>
                      <Input
                        value={med.instructions}
                        onChange={(e) => updateMedication(index, 'instructions', e.target.value)}
                        placeholder="e.g., Take with food"
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="border border-slate-200">
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <Label>Additional Notes</Label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                    placeholder="Any additional notes..."
                  />
                </div>
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/visits/${searchParams.get('visit_id')}`)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#4A238F] hover:bg-[#3A1B72] text-white"
                data-testid="submit-prescription-btn"
              >
                <Save className="mr-2 h-4 w-4" />
                Create Prescription
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="prescriptions-page">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
              Prescriptions
            </h1>
            <p className="text-slate-600 mt-2">Manage patient prescriptions</p>
          </div>
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="export-prescriptions-button">
                  <Download className="mr-2 h-4 w-4" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => handleExport('csv')}>
                  <FileText className="mr-2 h-4 w-4" />
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('pdf')}>
                  <FileText className="mr-2 h-4 w-4" />
                  Export as PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Dialog open={createDialogOpen} onOpenChange={(open) => {
              setCreateDialogOpen(open);
              if (!open) setIsMaximized(false);
            }}>
              <DialogTrigger asChild>
                <Button className="bg-[#4A238F] hover:bg-[#3A1B72] text-white" data-testid="new-prescription-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  New Prescription
                </Button>
              </DialogTrigger>
              <DialogContent 
                className={`overflow-y-auto transition-all duration-200 ${
                  isMaximized 
                    ? '!fixed !inset-2 !left-2 !top-2 !right-2 !bottom-2 !translate-x-0 !translate-y-0 !max-w-none !w-[calc(100vw-16px)] !h-[calc(100vh-16px)] !max-h-none' 
                    : 'max-w-2xl max-h-[90vh]'
                }`}
              >
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <DialogTitle>Create Prescription</DialogTitle>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsMaximized(!isMaximized)}
                      className="h-8 w-8 p-0 hover:bg-slate-100"
                      title={isMaximized ? "Minimize" : "Maximize"}
                    >
                      {isMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                    </Button>
                  </div>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Patient Selection - Always visible and required */}
                  <div className="space-y-2">
                    <Label>Select Patient *</Label>
                    <PatientSearchDropdown
                      patients={patients}
                      selectedPatientId={formData.patient_id}
                      onSelect={(patientId) => {
                        setFormData({...formData, patient_id: patientId, visit_id: ''});
                        setSelectedVisit(null);
                      }}
                      placeholder="Search patient by name, ID, or phone..."
                      required
                    />
                    {formData.patient_id && patients.find(p => p.id === formData.patient_id) && (
                      <p className="text-sm text-slate-500">
                        Selected: {patients.find(p => p.id === formData.patient_id)?.full_name} • 
                        {patients.find(p => p.id === formData.patient_id)?.patient_number}
                      </p>
                    )}
                  </div>

                  {/* Visit Selection - Optional, filtered by selected patient */}
                  <div className="space-y-2">
                    <Label>Link to Visit (Optional)</Label>
                    {formData.patient_id ? (
                      <Select value={formData.visit_id || 'none'} onValueChange={(val) => handleVisitChange(val === 'none' ? '' : val)}>
                        <SelectTrigger data-testid="visit-select">
                          <SelectValue placeholder="Select a visit (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No visit - standalone prescription</SelectItem>
                          {visits
                            .filter(v => v.patient_id === formData.patient_id)
                            .map(v => (
                              <SelectItem key={v.id} value={v.id}>
                                {v.visit_number} - {v.visit_type} - {v.status}
                                {['in_progress', 'in_consultation', 'checked_in'].includes(v.status) && ' ✓'}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="p-3 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-500">
                        Select a patient first to see their visits
                      </div>
                    )}
                    {selectedVisit && (
                      <p className="text-sm text-slate-500">
                        Visit: {selectedVisit.visit_number} • Complaint: {selectedVisit.chief_complaint}
                      </p>
                    )}
                  </div>
                  
                  {/* Diagnosis */}
                  <div className="space-y-2">
                    <Label>Diagnosis *</Label>
                    <Input
                      value={formData.diagnosis}
                      onChange={(e) => setFormData({...formData, diagnosis: e.target.value})}
                      placeholder="Primary diagnosis"
                      required
                    />
                  </div>
                  
                  {/* Medications (simplified for dialog) */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label>Medications *</Label>
                      <Button type="button" variant="ghost" size="sm" onClick={addMedication}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {medications.map((med, index) => (
                      <div key={index} className="grid grid-cols-4 gap-2 p-2 bg-slate-50 rounded">
                        <Input
                          value={med.name}
                          onChange={(e) => updateMedication(index, 'name', e.target.value)}
                          placeholder="Name"
                        />
                        <Input
                          value={med.dosage}
                          onChange={(e) => updateMedication(index, 'dosage', e.target.value)}
                          placeholder="Dosage"
                        />
                        <Select value={med.frequency} onValueChange={(v) => updateMedication(index, 'frequency', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Frequency" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="once daily">OD</SelectItem>
                            <SelectItem value="twice daily">BD</SelectItem>
                            <SelectItem value="three times daily">TDS</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          value={med.duration}
                          onChange={(e) => updateMedication(index, 'duration', e.target.value)}
                          placeholder="Duration"
                        />
                      </div>
                    ))}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Instructions</Label>
                    <textarea
                      value={formData.instructions}
                      onChange={(e) => setFormData({...formData, instructions: e.target.value})}
                      className="w-full min-h-[60px] px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
                      placeholder="General instructions..."
                    />
                  </div>
                  
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => { setCreateDialogOpen(false); resetForm(); }}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="bg-[#4A238F] hover:bg-[#3A1B72] text-white"
                      disabled={!formData.visit_id && !formData.patient_id}
                    >
                      Create Prescription
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Card className="border border-blue-200 bg-purple-100">
            <CardContent className="p-4 text-center">
              <Pill className="h-6 w-6 text-purple-700 mx-auto mb-2" />
              <p className="text-2xl font-bold text-blue-700">{stats.total || 0}</p>
              <p className="text-xs text-purple-700">Total</p>
            </CardContent>
          </Card>
          <Card className="border border-amber-200 bg-amber-50">
            <CardContent className="p-4 text-center">
              <Clock className="h-6 w-6 text-amber-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-amber-700">{stats.today || 0}</p>
              <p className="text-xs text-amber-600">Today</p>
            </CardContent>
          </Card>
          <Card className="border border-purple-200 bg-indigo-50">
            <CardContent className="p-4 text-center">
              <Clock className="h-6 w-6 text-purple-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-purple-700">{stats.active || 0}</p>
              <p className="text-xs text-purple-600">Active</p>
            </CardContent>
          </Card>
          <Card className="border border-emerald-200 bg-emerald-50">
            <CardContent className="p-4 text-center">
              <CheckCircle className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-emerald-700">{stats.dispensed || 0}</p>
              <p className="text-xs text-emerald-600">Dispensed</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search prescriptions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-prescriptions"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter">
              <Filter className="mr-2 h-4 w-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="dispensed">Dispensed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Prescriptions List */}
        <Card className="border border-slate-200">
          <CardContent className="p-0">
            {filteredPrescriptions.length === 0 ? (
              <div className="text-center py-12">
                <Pill className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No prescriptions found</p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredPrescriptions.map((rx) => (
                  <div 
                    key={rx.id} 
                    className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => handleViewPrescription(rx.id)}
                    data-testid={`prescription-card-${rx.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <Pill className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-slate-900">
                              {patients.find(p => p.id === rx.patient_id)?.full_name || 'Unknown Patient'}
                            </h3>
                            <Badge className={getStatusColor(rx.status)}>
                              {getStatusIcon(rx.status)}
                              <span className="ml-1">{rx.status}</span>
                            </Badge>
                          </div>
                          <p className="text-sm text-purple-600 font-medium">{rx.prescription_number}</p>
                          <p className="text-sm text-slate-600">{rx.diagnosis}</p>
                          <p className="text-xs text-slate-500">
                            {rx.medications?.length || 0} medication(s) • {new Date(rx.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        {rx.status === 'active' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-amber-600 hover:bg-amber-50"
                              onClick={() => handleEditPrescription(rx)}
                              data-testid={`edit-${rx.id}`}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-emerald-600 hover:bg-emerald-50"
                              onClick={() => handleUpdateStatus(rx.id, 'dispensed')}
                              data-testid={`dispense-${rx.id}`}
                            >
                              <CheckCircle className="mr-1 h-4 w-4" />
                              Dispense
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-rose-600 hover:bg-rose-50"
                              onClick={() => handleUpdateStatus(rx.id, 'cancelled')}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-purple-600 hover:bg-indigo-50"
                          onClick={() => handleDownloadPDF(rx.id, rx.prescription_number)}
                          data-testid={`download-pdf-${rx.id}`}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          PDF
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-violet-600 hover:bg-violet-50"
                          onClick={() => handleClonePrescription(rx)}
                          data-testid={`clone-${rx.id}`}
                        >
                          <Copy className="h-4 w-4 mr-1" />
                          Clone
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewPrescription(rx.id)}
                          data-testid={`view-${rx.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Prescription Detail View Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={(open) => {
          setViewDialogOpen(open);
          if (!open) {
            setPrescriptionDetail(null);
            if (prescriptionId) navigate('/prescriptions');
          }
        }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pill className="h-5 w-5 text-[#4A238F]" />
                Prescription Details
              </DialogTitle>
            </DialogHeader>
            {prescriptionDetail && (
              <div className="space-y-6">
                {/* Header Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm text-slate-600">Prescription #</p>
                    <p className="font-semibold">{prescriptionDetail.prescription_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Date</p>
                    <p className="font-semibold">{new Date(prescriptionDetail.created_at).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Status</p>
                    <Badge className={getStatusColor(prescriptionDetail.status)}>
                      {prescriptionDetail.status}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Visit #</p>
                    <p className="font-semibold">{prescriptionDetail.visit?.visit_number || 'N/A'}</p>
                  </div>
                </div>

                {/* Patient Info */}
                {prescriptionDetail.patient && (
                  <Card className="border border-slate-200">
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm font-semibold text-slate-700">Patient Information</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-sm text-slate-600">Name</p>
                          <p className="font-medium">{prescriptionDetail.patient.full_name}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Patient #</p>
                          <p className="font-medium">{prescriptionDetail.patient.patient_number}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Phone</p>
                          <p className="font-medium">{prescriptionDetail.patient.phone || 'N/A'}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Diagnosis */}
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-1">Diagnosis</p>
                  <p className="text-slate-900 bg-slate-50 p-3 rounded">{prescriptionDetail.diagnosis}</p>
                </div>

                {/* Medications */}
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-2">Medications ({prescriptionDetail.medications?.length || 0})</p>
                  <div className="space-y-2">
                    {prescriptionDetail.medications?.map((med, index) => (
                      <div key={index} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold text-slate-900">{med.name}</p>
                            <p className="text-sm text-slate-600">
                              {med.dosage} • {med.frequency} • {med.duration}
                            </p>
                            {med.instructions && (
                              <p className="text-sm text-slate-500 mt-1">Instructions: {med.instructions}</p>
                            )}
                          </div>
                          <Badge variant="outline" className="capitalize">{med.route}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Instructions */}
                {prescriptionDetail.instructions && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-1">General Instructions</p>
                    <p className="text-slate-900 bg-slate-50 p-3 rounded">{prescriptionDetail.instructions}</p>
                  </div>
                )}

                {/* Follow-up */}
                {prescriptionDetail.follow_up_date && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-1">Follow-up Date</p>
                    <p className="text-slate-900">{prescriptionDetail.follow_up_date}</p>
                  </div>
                )}

                {/* Notes */}
                {prescriptionDetail.notes && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-1">Notes</p>
                    <p className="text-slate-900 bg-slate-50 p-3 rounded">{prescriptionDetail.notes}</p>
                  </div>
                )}

                {/* Prescriber */}
                {prescriptionDetail.prescriber && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-slate-600">
                      <span className="font-medium">Prescribed by:</span> Dr. {prescriptionDetail.prescriber.full_name}
                      {prescriptionDetail.prescriber.specialization && ` (${prescriptionDetail.prescriber.specialization})`}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => handleDownloadPDF(prescriptionDetail.id, prescriptionDetail.prescription_number)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                  {prescriptionDetail.status === 'active' && (
                    <>
                      <Button
                        variant="outline"
                        className="text-amber-600 hover:bg-amber-50"
                        onClick={() => {
                          setViewDialogOpen(false);
                          handleEditPrescription(prescriptionDetail);
                        }}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => {
                          handleUpdateStatus(prescriptionDetail.id, 'dispensed');
                          setViewDialogOpen(false);
                        }}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Mark as Dispensed
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Prescription Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) {
            setEditingPrescription(null);
          }
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-[#4A238F]" />
                Edit Prescription {editingPrescription?.prescription_number}
              </DialogTitle>
            </DialogHeader>
            {editingPrescription && (
              <form onSubmit={handleUpdatePrescription} className="space-y-6">
                {/* Patient Info (read-only) */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">Patient</p>
                  <p className="font-semibold">{editingPrescription.patient?.full_name || patients.find(p => p.id === editingPrescription.patient_id)?.full_name}</p>
                </div>

                {/* Diagnosis */}
                <div className="space-y-2">
                  <Label htmlFor="edit_diagnosis">Diagnosis *</Label>
                  <Input
                    id="edit_diagnosis"
                    value={editFormData.diagnosis}
                    onChange={(e) => setEditFormData({...editFormData, diagnosis: e.target.value})}
                    placeholder="Primary diagnosis"
                    required
                    data-testid="edit-diagnosis-input"
                  />
                </div>

                {/* Medications */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Medications *</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addEditMedication}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Medication
                    </Button>
                  </div>
                  
                  {editMedications.map((med, index) => (
                    <Card key={index} className="border border-slate-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-slate-700">Medication {index + 1}</span>
                          {editMedications.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeEditMedication(index)}
                              className="text-rose-500 hover:text-rose-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          <div className="col-span-2 md:col-span-1">
                            <Label className="text-xs">Drug Name *</Label>
                            <Input
                              value={med.name}
                              onChange={(e) => updateEditMedication(index, 'name', e.target.value)}
                              placeholder="Medication name"
                              required
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Dosage *</Label>
                            <Input
                              value={med.dosage}
                              onChange={(e) => updateEditMedication(index, 'dosage', e.target.value)}
                              placeholder="e.g., 500mg"
                              required
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Frequency *</Label>
                            <Input
                              value={med.frequency}
                              onChange={(e) => updateEditMedication(index, 'frequency', e.target.value)}
                              placeholder="e.g., Twice daily"
                              required
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Duration *</Label>
                            <Input
                              value={med.duration}
                              onChange={(e) => updateEditMedication(index, 'duration', e.target.value)}
                              placeholder="e.g., 7 days"
                              required
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Route</Label>
                            <Select value={med.route} onValueChange={(v) => updateEditMedication(index, 'route', v)}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="oral">Oral</SelectItem>
                                <SelectItem value="topical">Topical</SelectItem>
                                <SelectItem value="injection">Injection</SelectItem>
                                <SelectItem value="inhaled">Inhaled</SelectItem>
                                <SelectItem value="sublingual">Sublingual</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Quantity</Label>
                            <Input
                              value={med.quantity}
                              onChange={(e) => updateEditMedication(index, 'quantity', e.target.value)}
                              placeholder="e.g., 14 tablets"
                            />
                          </div>
                          <div className="col-span-2 md:col-span-3">
                            <Label className="text-xs">Special Instructions</Label>
                            <Input
                              value={med.instructions}
                              onChange={(e) => updateEditMedication(index, 'instructions', e.target.value)}
                              placeholder="e.g., Take with food"
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* General Instructions */}
                <div className="space-y-2">
                  <Label htmlFor="edit_instructions">General Instructions</Label>
                  <textarea
                    id="edit_instructions"
                    value={editFormData.instructions}
                    onChange={(e) => setEditFormData({...editFormData, instructions: e.target.value})}
                    className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                    placeholder="General instructions for the patient..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit_follow_up">Follow-up Date</Label>
                    <Input
                      id="edit_follow_up"
                      type="date"
                      value={editFormData.follow_up_date}
                      onChange={(e) => setEditFormData({...editFormData, follow_up_date: e.target.value})}
                      data-testid="edit-follow-up-input"
                    />
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label htmlFor="edit_notes">Notes</Label>
                  <textarea
                    id="edit_notes"
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                    className="w-full min-h-[60px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                    placeholder="Additional notes..."
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="save-prescription-edit" className="bg-[#4A238F] hover:bg-[#3A1B72] text-white">
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
}
